import React, { useEffect, useState } from 'react';
import ReactPixel from 'react-facebook-pixel';
import { useDispatch, useSelector } from 'react-redux';
import { Title } from '../../components/Title';
import { Input } from './Input';
import { Button } from '../../components/Button';
import { setStep } from '../../store/view/view.reducers';
import { selectorStep } from '../../store/view/view.selectors';
import {
  Container,
  FormContainer,
  ImgWrapper,
  ModalContent,
  SuggestionsItem,
  SuggestionsList,
  AddressInputWrapper,
  YellowCard,
  LoginCard,
  LoginCardTitle,
  LoginCardText,
  AddressCheckRow,
  Subtitle,
  FieldLabel,
  FieldGroup,
  MarketingText,
  CheckboxLabel,
  CheckboxSmallText,
} from './AddressChecker.parts';
import { Modal } from '../../components/Modal';
import { ButtonWrap, ModalContentText, ModalContentTextNote } from '../../components/Modal/Modal.parts';
import { WaitingListForm } from '../../components/WaitingListForm';
import Pepper from '../../assets/img/Wonky-BoxPepper.png';
import { suggestionAddress, validateAddress, validateEmail } from './AddressChecker.helpers';
import { addValidAddress } from '../../store/cart/cart.reducers';
import TagManager from 'react-gtm-module';
import { setUserInfo } from '../../store/user/user.reducers';
import { ReactGTMOptions } from '../../constants';
import { checkIfUserExist } from '../../components/CreateAccountForm/CreateAccountForm.helpers';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { UserExistModal } from '../../components/UserExistModal';
import { PinterestTag } from '../../helpers/pinterest';
import { GtmTag } from '../../helpers/gtm';
import { ValidAddress } from '../../store/cart/cart.dto';

const AccountSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .trim()
    .strict()
    .required('Password is required')
    .min(8, 'Must be at least 8 characters'),
  passwordConf: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords do not match'),
});

export const AddressChecker = () => {
  const dispatch = useDispatch();
  const activeStep = useSelector(selectorStep);

  const [subStep, setSubStep] = useState<1 | 2>(1);
  const [validatedAddress, setValidatedAddress] = useState<ValidAddress | null>(null);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const [addressValue, setAddressValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestion, setSuggestion] = useState('');
  const [firstModal, setFirstModal] = useState(false);
  const [secondModal, setSecondModal] = useState(false);
  const [thirdModal, setThirdModal] = useState(false);
  const [userExistModal, setUserExistModal] = useState(false);
  const [userErrorMessage, setUserErrorMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [emailErrorModal, setEmailErrorModal] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');

  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: ReactGTMOptions({ title: 'Checkout: Address check' }),
    });
    ReactPixel.track('ViewContent', { content_name: 'Checkout: Address check' });
    PinterestTag.pageVisit({ page_name: 'Checkout: Address check' });
    PinterestTag.custom('initiate_checkout');
    GtmTag.beginCheckout();
  }, []);

  const addressCheckHandler = async () => {
    setLoading(true);

    const valid = await validateAddress(addressValue);

    if (valid?.error) {
      alert(valid.error);
      setLoading(false);
      return;
    }

    if (valid.result === 'The address is acceptable') {
      setValidatedAddress(valid);
      setSubStep(2);
    } else {
      setFirstModal(true);
    }

    setLoading(false);
  };

  const continueHandler = async (
    values: { email: string; password: string; passwordConf: string },
    marketingConsent: boolean,
  ) => {
    setLoading(true);

    const validationResult = await checkIfUserExist({ email: values.email });

    if (validationResult?.status === 409) {
      setUserErrorMessage(validationResult.error);
      setUserExistModal(true);
      setLoading(false);
      return;
    }
    if (validationResult) {
      alert(validationResult.error);
      setLoading(false);
      return;
    }

    try {
      const emailResult = await validateEmail(values.email);
      if (emailResult.error) {
        setEmailErrorMessage(emailResult.error);
        setEmailErrorModal(true);
        setLoading(false);
        return;
      }
    } catch {
      alert('An internal error occurred. Please try again.');
      setLoading(false);
      return;
    }

    // Identify user with Klaviyo
    _learnq.push(['identify', { $email: values.email }]);

    // Send Marketing Opt-In directly via Klaviyo Client API with profile inline.
    // Using _learnq.push doesn't work reliably on first visit because Klaviyo's
    // library sends /client/events/ before /client/profiles/, so the event fires
    // before the profile exists. The direct API call includes the profile email
    // so Klaviyo can create/find the profile and attribute the event in one request.
    if (marketingConsent) {
      fetch('https://a.klaviyo.com/client/events/?company_id=YydTxz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: 'Marketing Opt-In' } } },
              profile: { data: { type: 'profile', attributes: { email: values.email } } },
              properties: { email: values.email },
            },
          },
        }),
      }).catch(() => {});

      // Subscribe to marketing list
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=YydTxz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: values.email },
                },
              },
            },
            relationships: {
              list: {
                data: { type: 'list', id: 'X5q4Zp' },
              },
            },
          },
        }),
      }).catch(() => {});
    }

    // Copy __kla_id cookie to parent domain for cross-subdomain tracking
    setTimeout(() => {
      const klaId = document.cookie
        .split('; ')
        .find((row) => row.startsWith('__kla_id='))
        ?.split('=')[1];
      if (klaId) {
        document.cookie = `__kla_id=${klaId}; domain=.wonkybox.nz; path=/; max-age=63072000`;
      }
    }, 100);

    dispatch(setUserInfo({ email: values.email, password: values.password, marketing_consent: marketingConsent }));
    dispatch(addValidAddress({ ...validatedAddress!, address: addressValue }));
    dispatch(setStep(activeStep + 1));

    setLoading(false);
  };

  useEffect(() => {
    if (addressValue === suggestion) {
      return;
    }
    // if address input is cleared, clear suggestion state
    if (!addressValue.length) {
      setSuggestion('');
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      if (addressValue.length > 3) {
        setLoading(true);
        const suggestions = await suggestionAddress(addressValue);
        setSuggestions(suggestions);
        setSuggestion('');
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [addressValue]);

  const closeModals = () => {
    setFirstModal(false);
    setSecondModal(false);
    setThirdModal(false);
    setEmailErrorModal(false);
  };

  return (
    <Container>
      {subStep === 1 && (
        <>
          <YellowCard>
            <Title>LET'S SEE IF WONKY'S NEAR YOU</Title>
            <Subtitle>
              Type the first line of your address and select from the dropdown to check if we deliver to your area.
            </Subtitle>
            <AddressCheckRow>
              <AddressInputWrapper hasSuggestions={!!suggestions.length}>
                <Input
                  name="address"
                  value={addressValue}
                  onChange={e => setAddressValue(e.target.value)}
                  placeholder="Start typing your address"
                />
                {!!suggestions.length && (
                  <SuggestionsList>
                    {suggestions.map(item => (
                      <SuggestionsItem
                        key={item}
                        onClick={() => {
                          setAddressValue(item);
                          setSuggestions([]);
                          setSuggestion(item);
                        }}
                      >
                        {item}
                      </SuggestionsItem>
                    ))}
                  </SuggestionsList>
                )}
              </AddressInputWrapper>
              <Button loading={loading} type="button" disabled={!suggestion || loading} onClick={addressCheckHandler}>
                Check
              </Button>
            </AddressCheckRow>
          </YellowCard>

          <LoginCard>
            <LoginCardTitle>ALREADY HAVE AN ACCOUNT?</LoginCardTitle>
            <LoginCardText>
              You can skip the checkout and add or manage your boxes straight from your account dashboard.
            </LoginCardText>
            <Button
              type="button"
              onClick={() => window.location.assign('https://account.wonkybox.nz')}
            >
              LOGIN
            </Button>
          </LoginCard>
        </>
      )}

      {subStep === 2 && (
        <YellowCard>
          <Title>GOOD NEWS!</Title>
          <Subtitle>
            We deliver to your area
            {validatedAddress?.first_delivery_dow ? ` every ${validatedAddress.first_delivery_dow}` : ''}.
          </Subtitle>

          <Formik
            initialValues={{ email: '', password: '', passwordConf: '' }}
            validationSchema={AccountSchema}
            onSubmit={() => {}}
          >
            {({ values, handleChange, handleBlur, handleSubmit, errors, touched, isValid, dirty }) => (
              <FormContainer
                onSubmit={e => {
                  e.preventDefault();
                }}
              >
                <FieldGroup>
                  <FieldLabel>EMAIL ADDRESS</FieldLabel>
                  <Input
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your email address"
                    errorText={touched.email && errors.email ? errors.email : undefined}
                  />
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel>CREATE PASSWORD</FieldLabel>
                  <Input
                    type="password"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="At least 8 characters"
                    errorText={touched.password && errors.password ? errors.password : undefined}
                  />
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel>CONFIRM PASSWORD</FieldLabel>
                  <Input
                    type="password"
                    name="passwordConf"
                    value={values.passwordConf}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Confirm your password"
                    errorText={touched.passwordConf && errors.passwordConf ? errors.passwordConf : undefined}
                  />
                </FieldGroup>

                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={() => setMarketingConsent(!marketingConsent)}
                  />
                  <span>
                    Sign me up for emails with grower stories, what's in season, and a <strong>10% off code</strong> in my inbox.
                    <br />
                    <CheckboxSmallText>
                      Unsubscribe anytime. See our{' '}
                      <a href="https://wonkybox.nz/terms-of-service-policies" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </a>
                      .
                    </CheckboxSmallText>
                  </span>
                </CheckboxLabel>

                <Button
                  type="button"
                  loading={loading}
                  disabled={!(isValid && dirty) || loading}
                  onClick={() => {
                    handleSubmit();
                    if (isValid && dirty) {
                      continueHandler(values, marketingConsent);
                    }
                  }}
                >
                  CONTINUE
                </Button>
              </FormContainer>
            )}
          </Formik>
        </YellowCard>
      )}

      <UserExistModal
        isOpen={userExistModal}
        closeModals={() => setUserExistModal(false)}
        errorText={userErrorMessage}
      />
      <Modal
        width={'930px'}
        isOpen={firstModal}
        closeModal={closeModals}
        title="SORRY, WE DON'T CURRENTLY DELIVER TO YOUR POSTCODE."
      >
        <ModalContent>
          <ModalContentText>
            Fear not, we are expanding all the time. If you'd like to be notified as soon as we deliver to your area,
            please join our waiting list below.
          </ModalContentText>
          <ModalContentTextNote>Note: Please double check you entered your full postcode.</ModalContentTextNote>
          <ButtonWrap>
            <Button
              onClick={() => {
                setAddressValue('');
                setSuggestion('');
                setSuggestions([]);
                closeModals();
              }}
            >
              TRY ANOTHER ADDRESS
            </Button>
            <Button
              filled={false}
              onClick={() => {
                setSecondModal(true);
                setFirstModal(false);
              }}
            >
              JOIN OUR WAITING LIST
            </Button>
          </ButtonWrap>
        </ModalContent>
      </Modal>

      <Modal width={'930px'} isOpen={secondModal} closeModal={closeModals} title="ENTER YOUR DETAILS TO JOIN">
        <ModalContent>
          <WaitingListForm setThirdModal={setThirdModal} closeModal={closeModals} />
        </ModalContent>
      </Modal>

      <Modal
        width={'930px'}
        isOpen={thirdModal}
        closeModal={closeModals}
        title="THANK YOU FOR JOINING OUR WAITING LIST!"
      >
        <ModalContent>
          <ModalContentText>
            Once we expand our delivery services to your area, you will receive an email notification with all the
            exciting details.
          </ModalContentText>
          <ButtonWrap>
            <Button
              onClick={() => {
                setAddressValue('');
                closeModals();
              }}
            >
              TRY ANOTHER ADDRESS
            </Button>
            <Button
              filled={false}
              onClick={() => {
                alert('redirect');
                closeModals();
              }}
            >
              Go to home page
            </Button>
          </ButtonWrap>
          <ImgWrapper>
            <img src={Pepper} alt="pepper" />
          </ImgWrapper>
        </ModalContent>
      </Modal>

      <Modal
        width={'930px'}
        isOpen={emailErrorModal}
        closeModal={closeModals}
        title="INVALID EMAIL ADDRESS"
      >
        <ModalContent>
          <ModalContentText>{emailErrorMessage}</ModalContentText>
          <ButtonWrap>
            <Button onClick={closeModals}>OK</Button>
          </ButtonWrap>
        </ModalContent>
      </Modal>
    </Container>
  );
};
